import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { loadGoogleMaps } from "@/lib/google-maps-loader";
import {
  adjustMapsLifecycleCounter,
  emitMapsLifecycle,
  nextListenerTokenId,
  registerMapsInstance,
  unregisterMapsInstance,
  updateMapsInstance,
} from "@/lib/maps-lifecycle-bus";
import { auditAutocompleteInstance } from "@/lib/autocomplete-audit";

export interface ParsedAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
  lat: number;
  lng: number;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (parsed: ParsedAddress) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  /** Delay (ms) before propagating typed changes to the parent. Default 300. */
  debounceMs?: number;
}

export default function AddressAutocomplete({
  value,
  onChange,
  onPlaceSelected,
  placeholder = "Start typing a US address…",
  className,
  id,
  debounceMs = 300,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  // Token returned by `google.maps.event.addListener` for the
  // `place_changed` handler. We keep exactly one of these per Autocomplete
  // instance and remove it precisely on cleanup so we never leak duplicate
  // handlers across remounts / StrictMode double-invocations.
  const placeListenerRef = useRef<any>(null);
  // Tracks whether the component is currently mounted. Flipped off only
  // inside the deferred teardown so async callbacks that race a true
  // unmount can bail out safely.
  const mountedRef = useRef(true);
  // Timer that defers the actual Autocomplete teardown by one tick. A
  // StrictMode unmount/remount cycle re-runs this effect synchronously,
  // and the new run cancels this timer — so cleanup only ever executes
  // on a *real* unmount.
  const pendingTeardownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stable id used to publish per-instance status to the lifecycle bus,
  // so the dev panel (and any consumer of `getMapsInstanceStatuses()`)
  // can show whether this Autocomplete is initialized, whether a
  // place_changed listener token is currently held, and so on.
  const instanceIdRef = useRef<string>(`autocomplete-${Math.random().toString(36).slice(2, 8)}`);
  useEffect(() => {
    const id = instanceIdRef.current;
    registerMapsInstance("autocomplete", id);
    return () => unregisterMapsInstance(id);
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Local input state so typing stays responsive while we debounce the
  // upstream onChange (which may trigger geocoding in the parent).
  const [internalValue, setInternalValue] = useState(value);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextSyncRef = useRef(false);
  // Dedupe key for place_changed: if the same place fires twice in quick
  // succession (e.g. a duplicate listener slipped past the audit during a
  // reattach race), we apply coordinates exactly once per selection.
  const lastPlaceSignatureRef = useRef<string | null>(null);
  const lastPlaceAtRef = useRef<number>(0);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Keep local state in sync when the parent value changes externally
  // (e.g. a place is selected and the parent sets the formatted address).
  useEffect(() => {
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }
    setInternalValue(value);
  }, [value]);

  // Flush any pending debounce on unmount.
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  useEffect(() => {
    // Mark mounted and cancel any pending teardown scheduled by a
    // previous cleanup. In React StrictMode this synchronous re-run
    // happens right after the cleanup, so cancelling here keeps the
    // already-built Autocomplete instance alive across the dev-only
    // unmount/remount cycle.
    mountedRef.current = true;
    if (pendingTeardownRef.current) {
      clearTimeout(pendingTeardownRef.current);
      pendingTeardownRef.current = null;
      emitMapsLifecycle("autocomplete", "cleanup:cancelled", "StrictMode remount");
      updateMapsInstance(instanceIdRef.current, { pendingTeardown: false });
    }

    // Guard against React StrictMode's double-invoked effects: if an
    // Autocomplete instance is already attached to this input, do not
    // create a second one (which would trigger duplicate place_changed
    // events and a duplicate `pac-container` dropdown).
    if (autocompleteRef.current) {
      emitMapsLifecycle("autocomplete", "instance:reuse");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const google = await loadGoogleMaps();
        // Bail out if the component was truly unmounted while the loader
        // promise was in flight. `mountedRef` is only flipped off inside
        // the deferred teardown, so this also correctly survives the
        // StrictMode cleanup/re-run cycle.
        if (cancelled || !mountedRef.current || !inputRef.current) return;
        const placesLib: any = await google.maps.importLibrary("places");
        if (cancelled || !mountedRef.current || !inputRef.current) return;
        if (autocompleteRef.current) {
          if (mountedRef.current) setLoading(false);
          return;
        }
        const AutocompleteCtor = placesLib.Autocomplete ?? google.maps.places?.Autocomplete;
        if (!AutocompleteCtor) {
          throw new Error("Google Places Autocomplete unavailable");
        }
        const ac = auditAutocompleteInstance(
          new AutocompleteCtor(inputRef.current, {
            componentRestrictions: { country: "us" },
            fields: ["address_components", "formatted_address", "geometry"],
            types: ["address"],
          }),
        );
        // After constructing the instance, re-check mount: if we raced an
        // unmount, dispose the freshly built Autocomplete locally instead
        // of stashing it on the ref (where the deferred teardown would
        // never see it).
        if (cancelled || !mountedRef.current) {
          const gEvent = (window as any).google?.maps?.event;
          if (gEvent) {
            try { gEvent.clearInstanceListeners(ac); } catch { /* ignore */ }
          }
          emitMapsLifecycle(
            "autocomplete",
            "instance:dispose-cancelled",
            "unmounted before listener attach",
          );
          return;
        }
        // Defensive: if a listener somehow already exists for this
        // instance (e.g. a previous effect run we didn't clean), remove it
        // before attaching a new one so we never end up with duplicates.
        // Skip silently when the Maps event API is not (yet) available —
        // the listener will be cleared along with the instance once the
        // API is in scope.
        const gEventAttach = (window as any).google?.maps?.event;
        if (placeListenerRef.current) {
          if (gEventAttach) {
            try { gEventAttach.removeListener(placeListenerRef.current); } catch { /* ignore */ }
          } else {
            emitMapsLifecycle(
              "autocomplete",
              "listener:remove-skipped",
              "google.maps.event unavailable",
            );
          }
          placeListenerRef.current = null;
        }
        // If the Autocomplete instance has no `addListener` (mock or partial
        // load), bail out gracefully without attaching a place_changed handler.
        if (typeof ac?.addListener !== "function") {
          emitMapsLifecycle(
            "autocomplete",
            "listener:add-skipped",
            "addListener unavailable",
          );
          autocompleteRef.current = ac;
          adjustMapsLifecycleCounter("autocompleteInstances", 1);
          emitMapsLifecycle("autocomplete", "instance:create");
          updateMapsInstance(instanceIdRef.current, {
            initialized: true,
            hasPlaceListener: false,
            placeListenerToken: null,
          });
          if (mountedRef.current) setLoading(false);
          return;
        }
        const listener = ac.addListener("place_changed", () => {
          // place_changed can fire after the deferred teardown started
          // running (the listener removal happens mid-callback in some
          // edge cases). Skip all state work if we're no longer mounted.
          if (!mountedRef.current) return;
          const place = ac.getPlace();
          if (!place.address_components || !place.geometry?.location) return;

          const get = (type: string, short = false) => {
            const c = place.address_components!.find((c: any) => c.types.includes(type));
            return c ? (short ? c.short_name : c.long_name) : "";
          };
          const streetNumber = get("street_number");
          const route = get("route");
          const street = [streetNumber, route].filter(Boolean).join(" ");
          const city = get("locality") || get("sublocality") || get("postal_town") || get("administrative_area_level_2");
          const state = get("administrative_area_level_1", true);
          const zip = get("postal_code");
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          const formatted = street || place.formatted_address || "";

          // Per-selection dedupe: a Google place_id (or a lat/lng+address
          // composite fallback) uniquely identifies a selection. If we see
          // the same signature within a 500ms window, treat it as a duplicate
          // dispatch from a reattached/duplicate listener and skip.
          const placeId = (place as any).place_id ?? "";
          const signature = `${placeId}|${lat.toFixed(6)},${lng.toFixed(6)}|${formatted}`;
          const now = Date.now();
          if (
            lastPlaceSignatureRef.current === signature &&
            now - lastPlaceAtRef.current < 500
          ) {
            emitMapsLifecycle(
              "autocomplete",
              "listener:remove-skipped",
              "duplicate place_changed suppressed",
            );
            return;
          }
          lastPlaceSignatureRef.current = signature;
          lastPlaceAtRef.current = now;

          // Final mount re-check before mutating state / firing callbacks.
          if (!mountedRef.current) return;

          // Place selection should bypass debounce — propagate immediately.
          if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
            debounceTimer.current = null;
          }
          skipNextSyncRef.current = false;
          setInternalValue(formatted);
          onChangeRef.current(formatted);
          onPlaceSelected({ street: formatted, city, state, zip, lat, lng });
        });
        placeListenerRef.current = listener;
        autocompleteRef.current = ac;
        const tokenId = nextListenerTokenId();
        adjustMapsLifecycleCounter("autocompleteInstances", 1);
        adjustMapsLifecycleCounter("autocompleteListeners", 1);
        emitMapsLifecycle("autocomplete", "instance:create");
        emitMapsLifecycle("autocomplete", "listener:add", `place_changed (token #${tokenId})`);
        updateMapsInstance(instanceIdRef.current, {
          initialized: true,
          hasPlaceListener: true,
          placeListenerToken: tokenId,
        });
        if (mountedRef.current) setLoading(false);
      } catch (e: any) {
        if (cancelled || !mountedRef.current) return;
        emitMapsLifecycle("autocomplete", "error", e?.message || "load failed");
        setError(e?.message || "Could not load address lookup");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      // Defer the actual teardown one tick. If React StrictMode is about
      // to re-run this effect synchronously, the new run cancels this
      // timer before it fires and the existing instance is preserved.
      // On a real unmount nothing cancels it and teardown executes once,
      // deterministically.
      if (pendingTeardownRef.current) {
        clearTimeout(pendingTeardownRef.current);
      }
      emitMapsLifecycle("autocomplete", "cleanup:scheduled");
      updateMapsInstance(instanceIdRef.current, { pendingTeardown: true });
      pendingTeardownRef.current = setTimeout(() => {
        pendingTeardownRef.current = null;
        mountedRef.current = false;
        emitMapsLifecycle("autocomplete", "cleanup:run");
        const ac = autocompleteRef.current;
        const gEvent = (window as any).google?.maps?.event;
        if (placeListenerRef.current && gEvent) {
          let removeOk = true;
          let removeErr = "";
          try {
            gEvent.removeListener(placeListenerRef.current);
          } catch (e: any) {
            removeOk = false;
            removeErr = e?.message || String(e);
          }
          adjustMapsLifecycleCounter("autocompleteListeners", -1);
          emitMapsLifecycle(
            "autocomplete",
            "listener:remove",
            removeOk
              ? "place_changed (ok)"
              : `place_changed (failed: ${removeErr})`,
          );
        } else if (placeListenerRef.current && !gEvent) {
          emitMapsLifecycle(
            "autocomplete",
            "listener:remove-skipped",
            "google.maps.event unavailable at teardown",
          );
        }
        placeListenerRef.current = null;
        if (ac && gEvent) {
          try {
            gEvent.clearInstanceListeners(ac);
          } catch (e: any) {
            emitMapsLifecycle(
              "autocomplete",
              "error",
              `clearInstanceListeners failed: ${e?.message || String(e)}`,
            );
          }
        }
        if (autocompleteRef.current) {
          adjustMapsLifecycleCounter("autocompleteInstances", -1);
          emitMapsLifecycle("autocomplete", "instance:dispose");
        }
        autocompleteRef.current = null;
        updateMapsInstance(instanceIdRef.current, {
          initialized: false,
          hasPlaceListener: false,
          placeListenerToken: null,
          pendingTeardown: false,
        });
        document
          .querySelectorAll(".pac-container")
          .forEach((el) => el.remove());
      }, 0);
    };
  }, []);

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        id={id}
        value={internalValue}
        onChange={(e) => {
          const next = e.target.value;
          setInternalValue(next);
          if (debounceTimer.current) clearTimeout(debounceTimer.current);
          debounceTimer.current = setTimeout(() => {
            // Avoid the parent->child sync effect resetting our local
            // value to the same string we just sent up.
            skipNextSyncRef.current = true;
            onChangeRef.current(next);
            debounceTimer.current = null;
          }, debounceMs);
        }}
        placeholder={loading ? "Loading address lookup…" : placeholder}
        className={className}
        autoComplete="off"
      />
      {loading && (
        <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      )}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}
